import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RegistrationActivity } from '@app/shared/models/registration-activity.model';
import { PagedResult } from '@app/shared/models/paged-result';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RegistrationActivityTransition } from '../activity-transition/models/registration-activity-transition.model';
import { RegistrationActivityTransitionQueryViewModel } from '../activity-transition/models/registration-activity-transition-query-view-model';
import { RegistrationActivityQueryViewModel } from '../registration-activities/models/registration-activity-query-view-model.model';
import { AddEditActivityLookUp } from '../models/add-edit-activity-lookUp.model';

@Injectable({
  providedIn: 'root',
})
export class RegistrationActivityService {
  baseURL = environment.apiUrl + 'RegistrationActivity';

  constructor(private http: HttpClient) {}

  getAll(): Observable<RegistrationActivity[]> {
    return this.http.get<RegistrationActivity[]>(`${this.baseURL}`);
  }

  deleteRegistrationActivity(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseURL}/${id}`);
  }

  getById(id: number): Observable<RegistrationActivity> {
    return this.http.get<RegistrationActivity>(`${this.baseURL}/${id}`);
  }

  getByName(activityName: string): Observable<RegistrationActivity> {
    return this.http.get<RegistrationActivity>(
      `${this.baseURL}/${activityName}/Transition/NextActivities`
    );
  }

  addRegistrationActivity(
    registrationActivity: RegistrationActivity
  ): Observable<RegistrationActivity> {
    return this.http.post<RegistrationActivity>(
      `${this.baseURL}`,
      registrationActivity
    );
  }

  updateRegistrationActivity(
    registrationActivity: RegistrationActivity
  ): Observable<RegistrationActivity> {
    return this.http.put<RegistrationActivity>(
      `${this.baseURL}`,
      registrationActivity
    );
  }

  getAllActivitiesWithTransitions(): Observable<
    RegistrationActivityTransition[]
  > {
    return this.http.get<RegistrationActivityTransition[]>(
      `${this.baseURL}/transition`
    );
  }

  getWithTransitionById(
    id: number
  ): Observable<RegistrationActivityTransition> {
    return this.http.get<RegistrationActivityTransition>(
      `${this.baseURL}/${id}/Transition`
    );
  }

  addNewTransition(
    id: number,
    nextIds: number[]
  ): Observable<RegistrationActivityTransition> {
    return this.http.post<RegistrationActivityTransition>(
      `${this.baseURL}/${id}/Transition`,
      nextIds
    );
  }

  deleteTransition(id: number): Observable<boolean> {
    return this.http.delete<boolean>(`${this.baseURL}/${id}/Transition`);
  }

  updateTransition(
    currentId: number,
    nextIds: number[]
  ): Observable<RegistrationActivityTransition> {
    return this.http.put<RegistrationActivityTransition>(
      `${this.baseURL}/${currentId}/Transition`,
      nextIds
    );
  }

  private buildTransitionParams(
    query: RegistrationActivityTransitionQueryViewModel
  ): HttpParams {
    let params = new HttpParams()
      .set('pageIndex', query.pageIndex ?? 1)
      .set('pageSize', query.pageSize ?? 10);

    if (query.searchKey !== undefined) {
      params = params.set('searchKey', query.searchKey.toString());
    }

    if (query.search) {
      params = params.set('search', query.search);
    }

    if (query.sortBy !== undefined) {
      params = params.set('sortBy', query.sortBy.toString());
    }

    if (query.sortDirection !== undefined) {
      params = params.set('sortDirection', query.sortDirection.toString());
    }

    return params;
  }

  getPaginatedTransitions(
    query: RegistrationActivityTransitionQueryViewModel
  ): Observable<PagedResult<RegistrationActivityTransition>> {
    return this.http.get<PagedResult<RegistrationActivityTransition>>(
      `${this.baseURL}/PaginatedTransitions`,
      {
        params: this.buildTransitionParams(query),
      }
    );
  }

  exportTransitions(
    query: RegistrationActivityTransitionQueryViewModel
  ): Observable<Blob> {
    return this.http.get(`${this.baseURL}/ExportTransitions`, {
      params: this.buildTransitionParams(query),
      responseType: 'blob',
    });
  }

  getRegistrationActivitiesLookUp(): Observable<AddEditActivityLookUp> {
    return this.http.get<AddEditActivityLookUp>(
      `${this.baseURL}/RegistrationActivityLookUps`
    );
  }

  getPaginatedRegistrationActivities(
    query: RegistrationActivityQueryViewModel
  ): Observable<PagedResult<RegistrationActivity>> {
    let params = this.setQueryParams(query);

    return this.http.get<PagedResult<RegistrationActivity>>(
      `${this.baseURL}/Paginated`,
      { params }
    );
  }

  exportRegistrationActivities(
    query: RegistrationActivityQueryViewModel
  ): Observable<Blob> {
    let params = this.setQueryParams(query);

    return this.http.get(`${this.baseURL}/Export`, {
      params,
      responseType: 'blob',
    });
  }

  private setQueryParams(
    query: RegistrationActivityQueryViewModel
  ): HttpParams {
    let params = new HttpParams()
      .set('pageIndex', query.pageIndex ?? 0)
      .set('pageSize', query.pageSize ?? 20);

    if (query.searchKey)
      params = params.set('searchKey', query.searchKey.toString());
    if (query.search) params = params.set('search', query.search);
    if (query.sortBy !== undefined)
      params = params.set('sortBy', query.sortBy.toString());
    if (query.sortDirection !== undefined)
      params = params.set('sortDirection', query.sortDirection.toString());

    if (query.systemStatus !== undefined)
      params = params.set('systemStatus', query.systemStatus.toString());
    if (query.status !== undefined)
      params = params.set('status', query.status.toString());
    if (query.progressPoint !== undefined)
      params = params.set('progressPoint', query.progressPoint.toString());

    return params;
  }
}
